import ReactPixel from 'react-facebook-pixel';

// Define window fbq type for TypeScript
declare global {
  interface Window {
    fbq: any;
  }
}

/**
 * Checks if a specific Facebook Pixel ID is already initialized
 */
export function isPixelInitialized(pixelId: string): boolean {
  // Check if fbq exists in the window object
  if (typeof window === 'undefined' || typeof window?.fbq === 'undefined') {
    return false;
  }

  try {
    // Get all initialized pixels
    const initializedPixels = window?.fbq?.getState()?.pixels?.map(function (pixel: any) {
      return pixel.id;
    });

    // Check if our pixel ID exists in the array
    return initializedPixels.indexOf(pixelId) !== -1;
  } catch (error) {
    console.error('Error checking if pixel is initialized:', error);
    return false;
  }
}

/**
 * Initialize Facebook Pixel with the given Pixel ID if not already initialized
 */
export function initializePixel(pixelId: string): void {
  if (!pixelId) {
    console.warn('No Facebook Pixel ID provided');
    return;
  }

  // Don't initialize if already initialized
  if (isPixelInitialized(pixelId)) {
    console.log(`Facebook Pixel ${pixelId} already initialized`);
    return;
  }

  try {
    // Initialize with react-facebook-pixel
    const options = {
      autoConfig: true,
      debug: process.env.NODE_ENV !== 'production',
    };

    ReactPixel.init(pixelId, undefined, options);
    console.log(`Facebook Pixel ${pixelId} initialized`);
  } catch (error) {
    console.error('Error initializing Facebook Pixel:', error);
  }
}

/**
 * Track a page view with the Facebook Pixel
 */
export function trackPageView(): void {
  try {
    ReactPixel.pageView();
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
}

/**
 * Track a custom event with the Facebook Pixel
 */
export function trackEvent(event: string, data?: any): void {
  try {
    ReactPixel.track(event, data);
  } catch (error) {
    console.error(`Error tracking event ${event}:`, error);
  }
}
